/**
 * File List Table Component
 *
 * Displays files in a table format with download and delete actions
 * Supports both versioned (map/cad/roi) and non-versioned (learning/test) categories
 * Includes checkbox selection for batch operations
 */

import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
  Chip,
  Tooltip,
  Checkbox,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import { FileInfo, FileCategory, FileStatus, FILE_STATUS_LABELS, FILE_STATUS_COLORS } from '../../models/FileStorage';
import { FileStorageService } from '../../services/FileStorageService';
import { useNavigate } from 'react-router-dom';

interface FileListTableProps {
  files: FileInfo[];
  category: FileCategory;
  projectId?: string;
  onDownload: (filename: string, originalName: string) => void;
  onDownloadLatest: (originalName: string) => void;
  onDelete: (filename: string) => void;
  onBatchDelete?: (filenames: string[]) => void;
  onBatchDownload?: (filenames: string[]) => void;
  onEditMetadata?: (filename: string, description?: string, status?: FileStatus) => void;
}

/**
 * FileListTable Component
 *
 * Renders file list with different layouts:
 * - Versioned categories (map/cad/roi): Groups by original name with version info
 * - Non-versioned categories (learning/test): Flat list with CCTV ID
 */
export const FileListTable: React.FC<FileListTableProps> = ({
  files,
  category,
  projectId,
  onDownload,
  onDownloadLatest,
  onDelete,
  onBatchDelete,
  onBatchDownload,
  onEditMetadata,
}) => {
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

  // Check if category supports versioning
  const isVersioned = ['map', 'cad', 'roi'].includes(category);

  // Group files by original name for versioned categories (memoized)
  const fileGroups = useMemo(
    () => isVersioned ? groupFilesByOriginalName(files) : { '': files },
    [files, isVersioned]
  );

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allFilenames = files.map(file => file.filename);
      setSelectedFiles(new Set(allFilenames));
    } else {
      setSelectedFiles(new Set());
    }
  };

  const handleSelectFile = (filename: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(filename)) {
      newSelected.delete(filename);
    } else {
      newSelected.add(filename);
    }
    setSelectedFiles(newSelected);
  };

  const handleBatchDelete = () => {
    if (selectedFiles.size === 0) return;
    if (window.confirm(`선택한 ${selectedFiles.size}개의 파일을 삭제하시겠습니까?`)) {
      onBatchDelete?.(Array.from(selectedFiles));
      setSelectedFiles(new Set());
    }
  };

  const handleBatchDownload = () => {
    if (selectedFiles.size === 0) return;
    onBatchDownload?.(Array.from(selectedFiles));
    setSelectedFiles(new Set());
  };

  const handleOpenFile = async (originalName: string) => {
    if (category === 'map' && projectId) {
      // Map 파일인 경우 먼저 파일을 다운로드하고 map-editor로 이동
      try {
        // 최신 버전 다운로드
        const blob = await FileStorageService.downloadLatest(projectId, category, originalName);

        // blob이 유효한지 확인
        if (!blob) {
          throw new Error('파일 다운로드에 실패했습니다.');
        }

        // blob이 text 타입인지 확인
        if (blob.type && !blob.type.includes('json') && !blob.type.includes('text')) {
          throw new Error('잘못된 파일 형식입니다. JSON 파일이 아닙니다.');
        }

        const text = await blob.text();

        // 텍스트가 유효한 JSON인지 확인
        try {
          JSON.parse(text);
        } catch (parseError) {
          throw new Error('유효하지 않은 JSON 파일입니다.');
        }

        // Map ID 추출 (파일명에서 .json 제거)
        const mapId = originalName.replace(/\.json$/, '');

        // 맵 데이터를 localStorage에 저장하여 에디터에서 사용
        try {
          localStorage.setItem(`map-data-${mapId}`, text);
        } catch (storageError) {
          // QuotaExceededError: 용량 초과 시 기존 맵 데이터 정리 후 재시도
          const keysToRemove = Object.keys(localStorage).filter(k => k.startsWith('map-data-'));
          keysToRemove.forEach(k => localStorage.removeItem(k));
          localStorage.setItem(`map-data-${mapId}`, text);
        }

        // Map editor로 이동
        navigate(`/project/${projectId}/map-editor/${mapId}`);
      } catch (error) {
        console.error('파일 열기 실패:', error);
        const errorMessage = error instanceof Error ? error.message : '파일을 열 수 없습니다.';
        alert(errorMessage);
      }
    }
  };

  // Empty state
  if (files.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3, border: '2px dashed #ccc', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          업로드된 파일이 없습니다.
        </Typography>
      </Box>
    );
  }

  const isAllSelected = files.length > 0 && selectedFiles.size === files.length;
  const isSomeSelected = selectedFiles.size > 0 && selectedFiles.size < files.length;

  return (
    <Box>
      {/* Batch actions toolbar */}
      {selectedFiles.size > 0 && (onBatchDelete || onBatchDownload) && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="primary.contrastText">
            {selectedFiles.size}개 선택됨
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onBatchDownload && (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<GetAppIcon />}
                onClick={handleBatchDownload}
              >
                다운로드
              </Button>
            )}
            {onBatchDelete && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteSweepIcon />}
                onClick={handleBatchDelete}
              >
                선택 삭제
              </Button>
            )}
          </Box>
        </Box>
      )}

      <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell><Typography variant="subtitle2">파일명</Typography></TableCell>
              <TableCell><Typography variant="subtitle2">크기</Typography></TableCell>
              <TableCell><Typography variant="subtitle2">업로드 날짜</Typography></TableCell>
              <TableCell><Typography variant="subtitle2">업로드 유저</Typography></TableCell>
              <TableCell><Typography variant="subtitle2">비고</Typography></TableCell>
              <TableCell><Typography variant="subtitle2">상태</Typography></TableCell>
              {(category === 'learning' || category === 'test') && <TableCell><Typography variant="subtitle2">CCTV ID</Typography></TableCell>}
              <TableCell align="center"><Typography variant="subtitle2">작업</Typography></TableCell>
            </TableRow>
          </TableHead>
        <TableBody>
          {isVersioned ? (
            // Versioned categories: show grouped with latest on top
            Object.entries(fileGroups).map(([originalName, groupFiles]) => {
              const latestFile = groupFiles[0]; // Already sorted by version desc
              const hasMultipleVersions = groupFiles.length > 1;
              const isSelected = selectedFiles.has(latestFile.filename);

              return (
                <TableRow key={originalName} hover selected={isSelected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectFile(latestFile.filename)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{originalName}</Typography>
                      {hasMultipleVersions && (
                        <Chip
                          label={`${groupFiles.length}개 버전`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {FileStorageService.formatFileSize(latestFile.size_bytes)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {FileStorageService.formatDate(latestFile.upload_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {latestFile.uploaded_by_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {latestFile.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {latestFile.status && (
                      <Chip
                        label={FILE_STATUS_LABELS[latestFile.status]}
                        size="small"
                        color={FILE_STATUS_COLORS[latestFile.status]}
                      />
                    )}
                    {!latestFile.status && <Typography variant="body2">-</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {category === 'map' && (
                      <Tooltip title="파일 열기">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleOpenFile(originalName)}
                        >
                          <FolderOpenIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="최신 버전 다운로드">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onDownloadLatest(originalName)}
                      >
                        <GetAppIcon />
                      </IconButton>
                    </Tooltip>
                    {onEditMetadata && (
                      <Tooltip title="메타데이터 편집">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => onEditMetadata(latestFile.filename, latestFile.description, latestFile.status)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(latestFile.filename)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            // Non-versioned categories: flat list
            files.map((file) => {
              const isSelected = selectedFiles.has(file.filename);

              return (
                <TableRow key={file.filename} hover selected={isSelected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectFile(file.filename)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {file.original_name || file.filename}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {FileStorageService.formatFileSize(file.size_bytes)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {FileStorageService.formatDate(file.upload_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {file.uploaded_by_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {file.status && (
                      <Chip
                        label={FILE_STATUS_LABELS[file.status]}
                        size="small"
                        color={FILE_STATUS_COLORS[file.status]}
                      />
                    )}
                    {!file.status && <Typography variant="body2">-</Typography>}
                  </TableCell>
                  {(category === 'learning' || category === 'test') && (
                    <TableCell>
                      <Chip
                        label={file.cctv_id || '-'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Tooltip title="다운로드">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onDownload(file.filename, file.original_name)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    {onEditMetadata && (
                      <Tooltip title="메타데이터 편집">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => onEditMetadata(file.filename, file.description, file.status)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onDelete(file.filename)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

/**
 * Group files by original name for versioned categories
 * Sorts each group by version (descending)
 */
function groupFilesByOriginalName(files: FileInfo[]): Record<string, FileInfo[]> {
  const groups: Record<string, FileInfo[]> = {};

  files.forEach(file => {
    const key = file.original_name || file.filename;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(file);
  });

  // Sort each group by version (descending - latest first)
  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) => b.version.localeCompare(a.version));
  });

  return groups;
}
